/**
 * unmappedTestsPanelModel.ts
 * Phase 3 (quote-wo-schedule-lifecycle) — pure view-model for the
 * UnmappedTestsChip popover panel. Extracted so the chip/panel logic is unit
 * testable without rendering MUI (see unmappedTestsPanelModel.test.ts).
 *
 * Two severities:
 *   - unmapped (loud, orange): the lab test has NO catalog mapping, so its
 *     lines generated NO schedule tasks — they are missing from the plan.
 *   - unconfirmed (milder, amber): the test scheduled, but via a mapping
 *     estimate the lab has not confirmed yet (D4 — an estimate generating
 *     tasks beats a silent drop).
 */

export interface UnmappedTestsPanelModel {
  /** null = render nothing (both lists empty). */
  chipLabel: string | null;
  /** True when the chip reflects unmapped (loud) tests; false = unconfirmed-only (milder). */
  chipIsLoud: boolean;
  unmapped: string[];
  unconfirmed: string[];
  /** Plain-language explanation for the unmapped section. */
  unmappedExplanation: string;
  /** Milder note for the unconfirmed section (null when list empty). */
  unconfirmedNote: string | null;
  /** Admins get a deep link to the Test Mapping admin page. */
  showAdminLink: boolean;
  /** Non-admins are told who can fix it instead of getting a dead link. */
  contactAdminNote: string | null;
}

export const UNMAPPED_EXPLANATION =
  "These lab tests aren't linked to a scheduling catalog item, so their " +
  'work-order lines can’t generate schedule tasks — they are missing from ' +
  'this plan.';

export const UNCONFIRMED_NOTE =
  'These tests are scheduled using an unconfirmed mapping (a best-guess link ' +
  'to a catalog item the lab hasn’t verified yet).';

export const CONTACT_ADMIN_NOTE =
  'Ask a lab admin to link these tests on the Test Mapping admin page.';

export function buildUnmappedTestsPanelModel(
  unmapped: readonly string[],
  unconfirmed: readonly string[],
  isAdmin: boolean,
): UnmappedTestsPanelModel {
  const hasUnmapped = unmapped.length > 0;
  const hasUnconfirmed = unconfirmed.length > 0;

  let chipLabel: string | null = null;
  if (hasUnmapped) {
    chipLabel = `${unmapped.length} not schedulable`;
  } else if (hasUnconfirmed) {
    chipLabel = `${unconfirmed.length} unconfirmed mapping${unconfirmed.length !== 1 ? 's' : ''}`;
  }

  return {
    chipLabel,
    chipIsLoud: hasUnmapped,
    unmapped: [...unmapped],
    unconfirmed: [...unconfirmed],
    unmappedExplanation: UNMAPPED_EXPLANATION,
    unconfirmedNote: hasUnconfirmed ? UNCONFIRMED_NOTE : null,
    showAdminLink: isAdmin && (hasUnmapped || hasUnconfirmed),
    contactAdminNote: !isAdmin && hasUnmapped ? CONTACT_ADMIN_NOTE : null,
  };
}
